/**
 * Hook: useLeadHistory
 * Responsabilidade: Gerenciar o registro de eventos no histórico de leads
 * Princípios: Single Responsibility, DRY
 * 
 * Refatorado: Agora persiste no Supabase ao invés de localStorage
 */

import { useRef, useCallback } from 'react';
import { Lead, FunnelStage } from '../../../types';
import { leadHistoryService } from '../../../services/leadHistoryService';

interface LeadHistoryCache {
    value: number;
    stage: string;
    agent: string | null;
    name: string;
}

export const useLeadHistory = (funnelStages: FunnelStage[], organizationId?: string) => {
    // Cache de valores salvos no banco para comparação
    const lastSavedValues = useRef<Map<string, LeadHistoryCache>>(new Map());

    /**
     * Atualiza o cache com valores do banco após refresh
     */
    const updateCache = useCallback((leadId: string, value: number, stage: string, agent: string | null, name: string = '') => {
        lastSavedValues.current.set(leadId, { value, stage, agent, name });
    }, []);

    /**
     * Registra eventos de histórico comparando com valores salvos anteriormente
     * @param leadToSave Lead com os novos valores
     */
    const recordChanges = useCallback(async (leadToSave: Lead) => {
        if (!organizationId) {
            console.warn('[useLeadHistory] organizationId não disponível, histórico não será registrado');
            return;
        }

        const lastSaved = lastSavedValues.current.get(leadToSave.id);

        if (!lastSaved) {
            // Primeira vez salvando este lead, apenas atualiza cache
            return;
        }

        // Mudança de valor (com tolerância para arredondamento)
        if (Math.abs(lastSaved.value - leadToSave.value) > 0.01) {
            await leadHistoryService.addEvent({
                leadId: leadToSave.id,
                organizationId,
                type: 'value_updated',
                description: 'Valor atualizado',
                oldValue: `R$ ${lastSaved.value.toFixed(2)}`,
                newValue: `R$ ${leadToSave.value.toFixed(2)}`
            });
        }

        // Mudança de etapa
        if (lastSaved.stage !== leadToSave.stage) {
            const oldStage = funnelStages.find(s => s.id === lastSaved.stage);
            const newStage = funnelStages.find(s => s.id === leadToSave.stage);

            await leadHistoryService.addEvent({
                leadId: leadToSave.id,
                organizationId,
                type: 'stage_changed',
                description: 'Etapa alterada',
                oldValue: oldStage?.name || lastSaved.stage,
                newValue: newStage?.name || leadToSave.stage
            });
        }

        // Mudança de agente
        if (lastSaved.agent !== leadToSave.agent) {
            if (leadToSave.agent && !lastSaved.agent) {
                await leadHistoryService.addEvent({
                    leadId: leadToSave.id,
                    organizationId,
                    type: 'agent_assigned',
                    description: 'Atendente atribuído',
                    newValue: leadToSave.agent
                });
            } else if (!leadToSave.agent && lastSaved.agent) {
                await leadHistoryService.addEvent({
                    leadId: leadToSave.id,
                    organizationId,
                    type: 'agent_removed',
                    description: 'Atendente removido',
                    oldValue: lastSaved.agent
                });
            } else if (leadToSave.agent && lastSaved.agent) {
                await leadHistoryService.addEvent({
                    leadId: leadToSave.id,
                    organizationId,
                    type: 'agent_assigned',
                    description: 'Atendente alterado',
                    oldValue: lastSaved.agent,
                    newValue: leadToSave.agent
                });
            }
        }

        // NOTA: Mudanças de nome não são registradas no histórico para evitar poluição
        // O nome pode ser editado livremente sem gerar eventos
    }, [funnelStages, organizationId]);

    /**
     * Registra evento de criação de lead
     */
    const recordCreation = useCallback(async (leadId: string, userId?: string) => {
        if (!organizationId) {
            console.warn('[useLeadHistory] organizationId não disponível, histórico de criação não será registrado');
            return;
        }

        try {
            await leadHistoryService.addEvent({
                leadId,
                organizationId,
                type: 'created',
                description: 'Negociação criada',
                userId,
            });
        } catch (error) {
            console.error('[useLeadHistory] Falha ao registrar criação:', error);
        }
    }, [organizationId]);

    return {
        updateCache,
        recordChanges,
        recordCreation
    };
};
